import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Play, GraduationCap, CheckCircle2, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { LessonCard } from "@/components/academy/LessonCard";
import { LessonPlayerDialog } from "@/components/academy/LessonPlayerDialog";
import { mediaUrl, fmtDuration } from "@/lib/academy";

const FeaturedLesson = ({ lesson, onOpen }) => (
  <section className="grid lg:grid-cols-5 gap-6 rounded-3xl border border-border bg-card p-5 sm:p-7 overflow-hidden" data-testid="academy-featured">
    <div className="lg:col-span-2 flex flex-col justify-center space-y-3">
      <div className="text-xs font-mono uppercase tracking-widest text-primary">Start here</div>
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Welcome To Velopaths</h2>
      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">New to Velopaths? Start here. These short training videos will help you understand the platform and get the most from your partner account.</p>
      <button type="button" onClick={() => onOpen(lesson)} className="self-start inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold transition-transform duration-200 hover:scale-[1.03]" data-testid="academy-featured-play-btn">
        <Play className="w-4 h-4 fill-current" />{lesson.completed ? "Watch again" : "Watch the welcome video"}
      </button>
    </div>
    <button type="button" onClick={() => onOpen(lesson)} className="lg:col-span-3 group relative aspect-video rounded-2xl overflow-hidden bg-muted text-left" data-testid="academy-featured-card">
      {lesson.thumbnail_url && <img src={mediaUrl(lesson.thumbnail_url)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
      <span className="absolute inset-0 flex items-center justify-center"><span className="w-16 h-16 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover:scale-110"><Play className="w-7 h-7 ml-1 fill-current" /></span></span>
      <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5 text-white">
        <div className="font-heading text-lg sm:text-xl font-semibold">{lesson.title}</div>
        <div className="text-xs text-white/80 flex items-center gap-3 mt-1"><span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{fmtDuration(lesson.duration)}</span>{!lesson.video_url && <span className="rounded-full bg-amber-500 text-slate-900 font-semibold px-2 py-0.5">Video coming soon</span>}{lesson.completed && <span className="flex items-center gap-1 text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" />Completed</span>}</div>
      </div>
    </button>
  </section>
);

const ProgressCard = ({ completed, total }) => (
  <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 min-w-[240px] space-y-2" data-testid="academy-progress">
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Partner Academy Progress</div>
    <div className="text-sm">Completed: <b className="tabular-nums" data-testid="academy-progress-text">{completed} of {total} lessons</b></div>
    <Progress value={total ? (completed / total) * 100 : 0} className="h-2" data-testid="academy-progress-bar" />
  </div>
);

export default function PartnerAcademy() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try { const { data: d } = await api.get("/academy/lessons"); setData(d); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    document.body.dataset.assistantDock = "header";
    window.dispatchEvent(new Event("assistant-dock"));
    return () => { delete document.body.dataset.assistantDock; window.dispatchEvent(new Event("assistant-dock")); };
  }, []);

  const setDone = async (lesson, done) => {
    try {
      if (done) await api.post(`/academy/lessons/${lesson.id}/complete`); else await api.delete(`/academy/lessons/${lesson.id}/complete`);
      if (done) toast.success(`Lesson complete: ${lesson.title}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const lessons = useMemo(() => data?.lessons || [], [data]);
  const featured = lessons.find((l) => l.featured) || lessons[0];
  const shown = cat === "All" ? lessons : lessons.filter((l) => l.category === cat);
  const open = lessons.find((l) => l.id === openId) || null;

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-8" data-testid="partner-academy">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="flex items-center gap-2 text-primary"><GraduationCap className="w-5 h-5" /><span className="text-xs font-mono uppercase tracking-widest">Training center</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight" data-testid="academy-title">Partner Academy</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Learn how to grow your territory, create rider experiences, and generate revenue through the Velopaths platform.</p>
        </div>
        <ProgressCard completed={data.completed} total={data.total} />
      </header>

      {featured && <FeaturedLesson lesson={featured} onOpen={(l) => setOpenId(l.id)} />}

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-bold">Video Library</h2>
          <div className="flex flex-wrap gap-2" data-testid="academy-category-filters">
            {["All", ...data.categories].map((c) => (
              <button key={c} type="button" onClick={() => setCat(c)} className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors duration-200 ${cat === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/50"}`} data-testid={`academy-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}>{c}</button>
            ))}
          </div>
        </div>
        {shown.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="academy-empty">No lessons in this category yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="academy-lesson-grid">
            {shown.map((l, i) => <LessonCard key={l.id} lesson={l} index={i} onOpen={(x) => setOpenId(x.id)} />)}
          </div>
        )}
      </section>

      <LessonPlayerDialog lesson={open} onClose={() => setOpenId(null)} onComplete={(l) => setDone(l, true)} onUncomplete={(l) => setDone(l, false)} />
    </div>
  );
}
